import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import TextField from 'components/DesignSystem/TextField';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import StudyHorizon from 'components/Flux/Upload/Form/UploadAccordion/studyHorizon';
import OutputOptions from 'components/Flux/Upload/Form/UploadAccordion/outputOptions';
import PrevivazModel from 'components/Flux/Upload/Form/UploadAccordion/previvazModel';
import MultiScenarios from 'components/Flux/Upload/Form/UploadAccordion/multiScenarios';
import useFluxUploadFormActions from 'utils/hooks/flux/uploadFormActions';
import FileField from './FileField';

import './index.scss';

type SectionKey = 'horizon' | 'output' | 'previvaz' | 'scenarios';

const countLabel = (total: number, gender: 'f' | 'm' = 'f') => {
  if (total === 0) {
    return gender === 'f' ? 'Nenhuma' : 'Nenhum';
  }

  const suffix = total === 1 ? '' : 's';

  return `${total} selecionad${gender === 'f' ? 'a' : 'o'}${suffix}`;
};

export default function FluxFileUploadFormMobile() {
  const {
    form: {
      name, setName, filename, fileRef, reset, valid,
      endDate, outputOptions, multiScenarios, previvazModel,
    },
    fileNotReady,
    handleFileInput,
    verifyZipFile,
    submitStudy,
    isVerifying,
    isUploadLoading,
    modalTitle,
    modalBody,
    modalHeaderClass,
    showModal,
    setShowModal,
  } = useFluxUploadFormActions();

  const [openSection, setOpenSection] = useState<SectionKey | null>(null);

  const toggle = (key: SectionKey) => {
    setOpenSection(openSection === key ? null : key);
  };

  const horizonText = endDate
    ? `até ${new Date(endDate * 1000).toLocaleDateString('pt-BR')}`
    : 'Sem data final';

  return (
    <>
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
        }}
      >
        <Modal.Header closeButton className={modalHeaderClass}>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ whiteSpace: 'pre-line' }}>{modalBody}</p>
        </Modal.Body>
      </Modal>

      <section className="upload-form-mobile">
        <h2 className="upload-form-mobile-title">Novo Estudo · Configuração</h2>

        <div className="upload-form-mobile-field">
          <TextField
            className="upload-form-mobile-name-field"
            label="Nome do Estudo"
            type="text"
            placeholder="Digite o nome do estudo"
            value={name}
            onChange={(e) => { setName(e.target.value); }}
          />
        </div>

        <FileField filename={filename} fileRef={fileRef} onChange={handleFileInput} />

        <Section
          label="Horizonte do Estudo"
          valueText={horizonText}
          open={openSection === 'horizon'}
          onToggle={() => toggle('horizon')}
        >
          <StudyHorizon />
        </Section>

        <Section
          label="Opções de Saída"
          valueText={countLabel(outputOptions.length)}
          open={openSection === 'output'}
          onToggle={() => toggle('output')}
        >
          <OutputOptions />
        </Section>

        <Section
          label="Modelo Previvaz"
          valueText={previvazModel || 'Não definido'}
          open={openSection === 'previvaz'}
          onToggle={() => toggle('previvaz')}
        >
          <PrevivazModel />
        </Section>

        <Section
          label="Multi Cenários"
          valueText={countLabel(multiScenarios.length, 'm')}
          open={openSection === 'scenarios'}
          onToggle={() => toggle('scenarios')}
        >
          <MultiScenarios />
        </Section>

        <div className="upload-form-mobile-actions">
          <SheetActionButton
            variant="secondary"
            onClick={verifyZipFile}
            disabled={fileNotReady || isVerifying || showModal}
            loading={isVerifying}
          >
            Verificar Zip
          </SheetActionButton>
          <SheetActionButton
            variant="confirm"
            onClick={submitStudy}
            disabled={!valid || isUploadLoading}
            loading={isUploadLoading}
          >
            Enviar Estudo
          </SheetActionButton>
          <Button variant="link" onClick={reset}>
            Cancelar Estudo
          </Button>
        </div>
      </section>
    </>
  );
}
